import { useEffect, useId, useRef, useState } from "react";
import { Form, redirect, useActionData, useLoaderData, useNavigation, type ActionFunctionArgs, type LoaderFunctionArgs } from "react-router";
import { AvatarPicker } from "../../components/avatar-picker.jsx";
import { Loading } from "../../components/skeletons.jsx";
import { Deferred } from "../../components/states.jsx";
import { translate, useI18n } from "../../i18n/index.js";
import { isApiError, type WorkspaceUpdate } from "../../lib/api.js";
import { deferred } from "../../lib/deferred.js";
import { apiContext, invalidateSession, sessionContext } from "../../lib/session.js";
import { workspaceContext } from "../../lib/workspace.js";
import { Callout, FormError, Input, PageHeader, SaveBar, SettingsRow, SettingsSection, useFormDirty, useToast, Skeleton } from "../../ui/index.js";

/**
 * Settings: the workspace itself — its name, URL slug and avatar.
 *
 * The control plane decides who may change them (owners and admins); this page
 * only reads the caller's role to say so up front instead of failing on save.
 */

/** A rejected field is named so the form can mark that input rather than the page. */
type ActionResult = { ok?: string; error?: string; field?: "name" | "slug" | "logo" };
type WorkspaceSettings = { id: string; name: string; slug: string; logo: string | null };

export function workspaceSettingsLoader({ context }: LoaderFunctionArgs) {
  const me = context.get(sessionContext);
  const settings = context
    .get(apiContext)
    .workspace(context.get(workspaceContext).id)
    .get()
    .then((detail) => {
      const role = detail.members.find((m) => m.userId === me?.actor.id)?.role;
      const workspace: WorkspaceSettings = { id: detail.id, name: detail.name, slug: detail.slug, logo: detail.logo ?? null };
      return { workspace, canEdit: role === "owner" || role === "admin" };
    });
  return { settings: deferred(settings) };
}

export async function workspaceSettingsAction({ context, request }: ActionFunctionArgs) {
  const ws = context.get(workspaceContext);
  const form = await request.formData();
  const input: WorkspaceUpdate = { name: String(form.get("name") ?? "").trim(), slug: String(form.get("slug") ?? "").trim() };
  // The avatar is sent only when it changed; an empty value removes it.
  if (form.has("logo")) input.logo = String(form.get("logo")) || null;
  try {
    const updated = await context.get(apiContext).workspace(ws.id).update(input);
    invalidateSession(); // the sidebar reads the name, slug and avatar from /api/me
    // The old URL no longer resolves to this workspace, so the page moves with it.
    if (updated.slug !== ws.slug) return redirect(`/w/${updated.slug}/settings/general`);
    return { ok: translate("settings.workspace.saved") } satisfies ActionResult;
  } catch (err) {
    // A taken slug is the user's to fix in this form, so it belongs on the field.
    if (isApiError(err, "conflict")) return { error: translate("workspaceNew.slugTaken"), field: "slug" } satisfies ActionResult;
    if (isApiError(err, "validation_failed")) {
      const field = (err.details as { field?: string } | undefined)?.field;
      return { error: err.message, ...(field === "name" || field === "slug" || field === "logo" ? { field } : {}) } satisfies ActionResult;
    }
    if (isApiError(err, "workspace_forbidden") || isApiError(err, "forbidden")) return { error: err.message } satisfies ActionResult;
    throw err;
  }
}

function WorkspaceForm({ workspace, canEdit, busy, result }: { workspace: WorkspaceSettings; canEdit: boolean; busy: boolean; result: ActionResult | undefined }) {
  const { t } = useI18n();
  const [logo, setLogo] = useState(workspace.logo);
  const [unreadable, setUnreadable] = useState(false);
  const nameId = useId();
  const slugId = useId();
  const logoError = unreadable ? t("avatar.unreadable") : result?.field === "logo" ? result.error : null;
  const form = useFormDirty();

  return (
    <Form ref={form.ref} method="post" className="dt-stack dt-gap-4" onInput={form.check} onChange={form.check}>
      <SettingsSection>
        <SettingsRow label={t("settings.workspace.avatar")} error={logoError}>
          <AvatarPicker
            name={workspace.name}
            value={logo}
            disabled={!canEdit}
            onChange={(next) => {
              setLogo(next);
              setUnreadable(false);
            }}
            onUnreadable={() => setUnreadable(true)}
          />
          {logo !== workspace.logo && <input type="hidden" name="logo" value={logo ?? ""} />}
        </SettingsRow>
        <SettingsRow label={t("workspaceNew.name")} labelFor={nameId} error={result?.field === "name" ? result.error : null}>
          <Input id={nameId} name="name" defaultValue={workspace.name} aria-invalid={result?.field === "name" || undefined} disabled={!canEdit} required />
        </SettingsRow>
        <SettingsRow label={t("workspaceNew.slug")} labelFor={slugId} error={result?.field === "slug" ? result.error : null}>
          <Input
            id={slugId}
            name="slug"
            defaultValue={workspace.slug}
            aria-invalid={result?.field === "slug" || undefined}
            disabled={!canEdit}
            pattern="[a-z0-9]([a-z0-9-]*[a-z0-9])?"
            required
          />
        </SettingsRow>
      </SettingsSection>
      <SaveBar open={canEdit && form.dirty} message={t("common.unsavedChanges")} saveLabel={t("settings.workspace.save")} busy={busy} />
    </Form>
  );
}

function WorkspaceFormSkeleton() {
  const { t } = useI18n();
  return (
    <Loading>
      <SettingsSection>
        <SettingsRow label={t("settings.workspace.avatar")}>
          <Skeleton shape="block" width={40} height={40} />
        </SettingsRow>
        <SettingsRow label={t("workspaceNew.name")}>
          <Skeleton shape="input" />
        </SettingsRow>
        <SettingsRow label={t("workspaceNew.slug")}>
          <Skeleton shape="input" />
        </SettingsRow>
      </SettingsSection>
    </Loading>
  );
}

export function WorkspaceSettingsSkeleton() {
  const { t } = useI18n();
  return (
    <>
      <PageHeader title={t("settings.workspace.title")} description={t("settings.workspace.description")} />
      <WorkspaceFormSkeleton />
    </>
  );
}

export function WorkspaceSettingsRoute() {
  const { settings } = useLoaderData<typeof workspaceSettingsLoader>();
  const result = useActionData() as ActionResult | undefined;
  const busy = useNavigation().state === "submitting";
  const { t } = useI18n();
  const toast = useToast();
  const announced = useRef<ActionResult>(undefined);
  useEffect(() => {
    if (!result?.ok || announced.current === result) return;
    announced.current = result;
    toast.success(result.ok);
  }, [result, toast]);
  // Re-keyed on what the server stored, so a successful save resets the form to it
  // while a rejected one keeps what the user typed.
  const stored = (w: WorkspaceSettings) => `${w.name}\n${w.slug}\n${w.logo?.length ?? 0}:${w.logo?.slice(-24) ?? ""}`;
  return (
    <>
      <PageHeader title={t("settings.workspace.title")} description={t("settings.workspace.description")} />
      <Deferred data={settings} fallback={<WorkspaceFormSkeleton />}>
        {({ workspace, canEdit }) => (
          <>
            {!canEdit && <Callout tone="info">{t("settings.workspace.readOnly")}</Callout>}
            {/* Errors that name no field still need somewhere to land. */}
            {result?.error && !result.field && <FormError message={result.error} />}
            <WorkspaceForm key={stored(workspace)} workspace={workspace} canEdit={canEdit} busy={busy} result={result} />
          </>
        )}
      </Deferred>
    </>
  );
}
